// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Solar System mode in the browser (owner, 2026-11-04;
 * docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md).
 *
 * - The **Solar System** button beside Browse fades the globe out and the
 *   orrery in: the Sun, the eight planets, the Moon, Ceres and Pluto where
 *   they are today, at true distances with Pluto's orbit 5 m across, or at
 *   true sizes. A card over it has the layout switch, the clock and a chip
 *   per body.
 * - **A body** clicked (or its chip) fades to that body's globe with its
 *   first dataset, and Browse keeps to that body's rows (and its moons').
 *   A strip at the top says where you are and offers the way back to the
 *   orrery, a flight to another body, and the way out.
 * - **Fly to…** flies faster than light, for 0.5 s (the Earth to the
 *   Moon) to 5 s (the Sun to Pluto), with star streaks, and says on
 *   arrival how many times the speed of light that was and how long light
 *   takes. The first flight on a device asks whether to fly or to fade;
 *   the answer is kept, and the menu can change it.
 *
 * The page's own parts (datasets, Browse, the globe's place on screen)
 * come through {@link SolarModeHost}, so this file knows nothing of main.ts.
 */

import type * as THREE from 'three'
import { getLocale, t, type MessageKey } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'
import { SOLAR_BODIES, solarBody, solarBodyOfDatasetBody, type SolarBodyId } from '../services/solarSystem/bodies'
import type { OrreryLayout } from '../services/solarSystem/layout'
import { lightSeconds, splitDuration } from '../services/solarSystem/flight'
import { createSolarSystemView, FADE_MS, type SolarViewHandle } from './solarSystemView'
import type { SolarPresenter } from '../services/solarSystem/presenter'

export type SolarModeState = 'off' | 'overview' | 'body'
export type SolarTravel = 'fly' | 'fade'

export type { SolarPresenter } from '../services/solarSystem/presenter'

export interface SolarModeHost {
  container: HTMLElement
  /** The controls' layer: the canvas goes under it, the card and strip into it. */
  uiRoot: HTMLElement
  loadThree(): Promise<typeof THREE>
  /** The dataset on the main globe, or null. */
  currentDatasetId(): string | null
  /** The body that dataset shows, as offEarthBody says it (null = the Earth, or nothing loaded). */
  currentDatasetBody(): string | null
  datasetExists(id: string): boolean
  /** A plan lock stands in the way (the Sun's live stream on a free account). */
  datasetLocked(id: string): Promise<boolean>
  loadDataset(id: string): Promise<void>
  /** The plain Earth, nothing loaded. */
  clearDataset(): Promise<void>
  /** Browse keeps to these body names, or to everything with null. */
  setBrowseBodies(bodies: readonly string[] | null): void
  /** The main globe on screen, CSS px within `container`. */
  globeOnScreen(): { cx: number; cy: number; radiusPx: number }
  /** The library picture of a body's sphere (an equirectangular image), or null. */
  pictureUrl(sosId: string): string | null
  /** Waits for the library index, so pictureUrl can answer. */
  libraryReady(): Promise<void>
  announce(message: string): void
  /** A short message where the viewer is looking (the page, or the headset's dock). */
  notice?(message: string): void
  /**
   * Whether this viewer may change the dataset (in a meeting the presenter
   * chooses it: the orrery can still be looked at, a visit is refused).
   */
  mayChangeDataset?(): boolean
  /** The mode changed (the Solar System button's pressed look). */
  onChange?(state: SolarModeState): void
}

export interface SolarModeHandle {
  readonly state: SolarModeState
  readonly body: SolarBodyId | null
  readonly view: SolarViewHandle | null
  readonly travel: SolarTravel | null
  /** The orrery's layout, clock and speed, as the presenter has them (defaults before the first one). */
  readonly layout: OrreryLayout
  readonly timeMs: number
  readonly playing: boolean
  /** The clock's speed, as its label's message key. */
  readonly rateKey: MessageKey
  /** A flight waits for the comfort answer (asked in the scene): where to. */
  readonly asking: SolarBodyId | null
  setLayout(layout: OrreryLayout): void
  today(): void
  togglePlay(): void
  cycleRate(): void
  /** The comfort answer, or null to drop the flight. */
  answerTravel(choice: SolarTravel | null): void
  /** The presenter drawing now, if any (for checks). */
  readonly presenterNow: SolarPresenter | null
  /** The picture that dresses a body's sphere (for any presenter), or null for its colour. */
  pictureUrl(id: SolarBodyId): string | null
  /** An XR session draws the orrery from now on (null: the browser again). */
  attachPresenter(presenter: SolarPresenter | null): void
  /** The Solar System button: in from off, out from the orrery, back to the orrery from a body. */
  toggle(): Promise<void>
  enter(): Promise<void>
  leave(): Promise<void>
  visit(id: SolarBodyId): Promise<void>
  /** Fly (or fade, by the kept choice) from the body shown to another one. */
  travelTo(id: SolarBodyId): Promise<void>
  setTravel(travel: SolarTravel): void
  /** main.ts: the main globe's dataset changed (by any path). */
  datasetChanged(): void
  relabel(): void
}

const TRAVEL_KEY = 'ayni.solar.travel'
const RATES: ReadonlyArray<{ days: number; key: MessageKey }> = [
  { days: 1, key: 'solar.clock.rate.day' },
  { days: 7, key: 'solar.clock.rate.week' },
  { days: 30, key: 'solar.clock.rate.month' },
  { days: 365, key: 'solar.clock.rate.year' },
]
/** How long the arrival message stays, ms. */
const ARRIVAL_MS = 7000
/** The longest the landing waits for its dataset before showing what there is, ms. */
const LANDING_WAIT_MS = 6000

const BODY_NAME_KEYS: Record<SolarBodyId, MessageKey> = {
  sun: 'solar.body.sun',
  mercury: 'solar.body.mercury',
  venus: 'solar.body.venus',
  earth: 'solar.body.earth',
  moon: 'solar.body.moon',
  mars: 'solar.body.mars',
  ceres: 'solar.body.ceres',
  jupiter: 'solar.body.jupiter',
  saturn: 'solar.body.saturn',
  uranus: 'solar.body.uranus',
  neptune: 'solar.body.neptune',
  pluto: 'solar.body.pluto',
}

export function solarBodyName(id: SolarBodyId): string {
  return t(BODY_NAME_KEYS[id])
}

function readTravel(): SolarTravel | null {
  try {
    const v = localStorage.getItem(TRAVEL_KEY)
    return v === 'fly' || v === 'fade' ? v : null
  } catch {
    return null
  }
}

function writeTravel(v: SolarTravel): void {
  try {
    localStorage.setItem(TRAVEL_KEY, v)
  } catch {
    // A private window: the choice lasts this visit.
  }
}

/** A light-time in words: "1.3 s", "8 min 19 s", "4 h 05 min". */
export function lightTimeText(seconds: number): string {
  const { h, m, s } = splitDuration(seconds)
  const nf = (n: number, digits = 0) => new Intl.NumberFormat(getLocale(), { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n)
  if (h > 0) return t('solar.time.hm', { h: nf(h), m: String(m).padStart(2, '0') })
  if (m > 0) return t('solar.time.ms', { m: nf(m), s: String(s).padStart(2, '0') })
  return t('solar.time.s', { s: nf(s, 1) })
}

/** The arrival message for a trip of `distanceKm` that took `flightS`. */
export function arrivalText(distanceKm: number, flightS: number): string {
  const light = lightSeconds(distanceKm)
  const times = light / flightS
  const speed = new Intl.NumberFormat(getLocale(), { maximumFractionDigits: times < 10 ? 1 : 0 }).format(times < 10 ? times : Math.round(times))
  return t('solar.arrival', { speed, time: lightTimeText(light) })
}

export function createSolarSystemMode(host: SolarModeHost): SolarModeHandle {
  let state: SolarModeState = 'off'
  let body: SolarBodyId | null = null
  let view: SolarViewHandle | null = null
  let viewLoading: Promise<SolarViewHandle> | null = null
  let busy = false
  let travel: SolarTravel | null = readTravel()
  /** The Earth's dataset when the mode began (or when the Earth was last left), for a visit back. */
  let earthDatasetId: string | null = null
  /** A dataset change this file asked for, not to be mistaken for the user's. */
  let ownLoads = 0
  let arrivalTimer = 0
  /** The XR session's presenter while one runs. */
  let xr: SolarPresenter | null = null
  let asking: SolarBodyId | null = null
  let askResolve: ((v: SolarTravel | null) => void) | null = null
  /** The presenter in use now. */
  async function presenter(): Promise<SolarPresenter> {
    return xr ?? (await ensureView())
  }
  function current(): SolarPresenter | null {
    return xr ?? view
  }

  // ---------------------------------------------------------------- chrome

  const card = document.createElement('section')
  card.id = 'solar-card'
  card.className = 'solar-card'
  card.hidden = true
  const strip = document.createElement('div')
  strip.id = 'solar-strip'
  strip.className = 'solar-strip'
  strip.hidden = true
  const toast = document.createElement('div')
  toast.id = 'solar-arrival'
  toast.className = 'solar-arrival'
  toast.setAttribute('role', 'status')
  toast.hidden = true
  const comfort = document.createElement('div')
  comfort.id = 'solar-comfort'
  comfort.className = 'solar-comfort'
  comfort.hidden = true
  host.uiRoot.append(card, strip, toast, comfort)

  function renderCard(): void {
    const p = current()
    const layout: OrreryLayout = p?.layout ?? 'distances'
    const playing = p?.playing ?? false
    const rate = RATES.find((r) => r.days === (p?.rate ?? 1)) ?? RATES[0]
    card.setAttribute('aria-label', t('solar.title'))
    card.innerHTML = `
      <div class="solar-card-head">
        <span class="solar-card-title">${escapeHtml(t('solar.title'))}</span>
        <span class="solar-card-date" id="solar-date"></span>
        <button type="button" class="solar-icon-btn" id="solar-close" title="${escapeAttr(t('solar.close.aria'))}" aria-label="${escapeAttr(t('solar.close.aria'))}">&#x2715;</button>
      </div>
      <div class="solar-card-row">
        <div class="solar-seg" role="group" aria-label="${escapeAttr(t('solar.layout.aria'))}">
          <button type="button" id="solar-layout-distances" aria-pressed="${layout === 'distances'}" title="${escapeAttr(t('solar.layout.distances.help'))}">${escapeHtml(t('solar.layout.distances'))}</button>
          <button type="button" id="solar-layout-sizes" aria-pressed="${layout === 'sizes'}" title="${escapeAttr(t('solar.layout.sizes.help'))}">${escapeHtml(t('solar.layout.sizes'))}</button>
        </div>
        <div class="solar-clock" role="group" aria-label="${escapeAttr(t('solar.clock.aria'))}">
          <button type="button" id="solar-today">${escapeHtml(t('solar.clock.today'))}</button>
          <button type="button" id="solar-play" aria-pressed="${playing}" title="${escapeAttr(t(playing ? 'solar.clock.pause' : 'solar.clock.play'))}" aria-label="${escapeAttr(t(playing ? 'solar.clock.pause' : 'solar.clock.play'))}">${playing ? '&#x275A;&#x275A;' : '&#x25B6;&#xFE0E;'}</button>
          <button type="button" id="solar-rate" title="${escapeAttr(t('solar.clock.rate.aria'))}">${escapeHtml(t(rate.key))}</button>
        </div>
      </div>
      <div class="solar-bodies" role="group" aria-label="${escapeAttr(t('solar.visit.aria'))}">
        ${SOLAR_BODIES.map((b) => `<button type="button" class="solar-body-chip" data-body="${b.id}"><span class="solar-dot" style="background:#${b.colour.toString(16).padStart(6, '0')}" aria-hidden="true"></span>${escapeHtml(solarBodyName(b.id))}</button>`).join('')}
      </div>
      <p class="solar-hint">${escapeHtml(t(layout === 'distances' ? 'solar.hint.distances' : 'solar.hint.sizes'))}</p>`
    syncDate()
  }

  function syncDate(): void {
    const el = card.querySelector('#solar-date')
    const p = current()
    if (!el || !p) return
    el.textContent = solarDateText(p.timeMs)
  }

  card.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('button')
    if (!target || !current()) return
    if (target.id === 'solar-close') return void handle.leave()
    if (target.id === 'solar-layout-distances' || target.id === 'solar-layout-sizes') return handle.setLayout(target.id === 'solar-layout-sizes' ? 'sizes' : 'distances')
    if (target.id === 'solar-today') return handle.today()
    if (target.id === 'solar-play') return handle.togglePlay()
    if (target.id === 'solar-rate') return handle.cycleRate()
    const chip = target.dataset.body as SolarBodyId | undefined
    if (chip) void handle.visit(chip)
  })

  function renderStrip(): void {
    const where = body ? t('solar.strip.on', { body: solarBodyName(body) }) : t('solar.title')
    const others = SOLAR_BODIES.filter((b) => b.id !== body)
    strip.innerHTML = `
      <span class="solar-strip-where">${escapeHtml(where)}</span>
      <button type="button" class="solar-strip-btn" id="solar-strip-overview">${escapeHtml(t('solar.strip.overview'))}</button>
      <button type="button" class="solar-strip-btn" id="solar-strip-fly" aria-haspopup="menu" aria-expanded="false" aria-controls="solar-fly-menu">${escapeHtml(t('solar.strip.fly'))}</button>
      <button type="button" class="solar-icon-btn" id="solar-strip-close" title="${escapeAttr(t('solar.strip.close.aria'))}" aria-label="${escapeAttr(t('solar.strip.close.aria'))}">&#x2715;</button>
      <div class="solar-fly-menu" id="solar-fly-menu" role="menu" aria-label="${escapeAttr(t('solar.fly.menu.aria'))}" hidden>
        ${others.map((b) => `<button type="button" role="menuitem" class="solar-fly-item" data-body="${b.id}"><span class="solar-dot" style="background:#${b.colour.toString(16).padStart(6, '0')}" aria-hidden="true"></span>${escapeHtml(solarBodyName(b.id))}</button>`).join('')}
        <div class="solar-travel" role="group" aria-label="${escapeAttr(t('solar.travel.label'))}">
          <span>${escapeHtml(t('solar.travel.label'))}</span>
          <button type="button" class="solar-travel-btn" data-travel="fly" aria-pressed="${travel === 'fly'}">${escapeHtml(t('solar.travel.fly'))}</button>
          <button type="button" class="solar-travel-btn" data-travel="fade" aria-pressed="${travel === 'fade'}">${escapeHtml(t('solar.travel.fade'))}</button>
        </div>
      </div>`
  }

  function setMenuOpen(open: boolean): void {
    const menu = strip.querySelector<HTMLElement>('#solar-fly-menu')
    const btn = strip.querySelector<HTMLElement>('#solar-strip-fly')
    if (!menu || !btn) return
    menu.hidden = !open
    btn.setAttribute('aria-expanded', String(open))
    if (open) menu.querySelector<HTMLElement>('.solar-fly-item')?.focus()
  }

  strip.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('button')
    if (!target) return
    if (target.id === 'solar-strip-overview') return void handle.enter()
    if (target.id === 'solar-strip-close') return void handle.leave()
    if (target.id === 'solar-strip-fly') {
      const menu = strip.querySelector<HTMLElement>('#solar-fly-menu')
      return setMenuOpen(!!menu?.hidden)
    }
    const tr = target.dataset.travel as SolarTravel | undefined
    if (tr) {
      handle.setTravel(tr)
      renderStrip()
      return setMenuOpen(true)
    }
    const to = target.dataset.body as SolarBodyId | undefined
    if (to) {
      setMenuOpen(false)
      void handle.travelTo(to)
    }
  })
  strip.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setMenuOpen(false)
  })
  document.addEventListener('click', (e) => {
    // The path, not contains(): a press inside may have redrawn the strip and detached its own target.
    if (!strip.hidden && !e.composedPath().includes(strip)) setMenuOpen(false)
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state === 'overview' && !busy && comfort.hidden) void handle.leave()
  })

  function showArrival(text: string): void {
    window.clearTimeout(arrivalTimer)
    toast.textContent = text
    toast.hidden = false
    toast.classList.remove('leaving')
    host.announce(text)
    arrivalTimer = window.setTimeout(() => {
      toast.classList.add('leaving')
      arrivalTimer = window.setTimeout(() => {
        toast.hidden = true
        toast.classList.remove('leaving')
      }, 600)
    }, ARRIVAL_MS)
  }

  function hideArrival(): void {
    window.clearTimeout(arrivalTimer)
    toast.hidden = true
    toast.classList.remove('leaving')
  }

  /** The first flight on a device: fly, or fade there instead. Resolves with the choice, or null when dismissed. */
  function askTravel(to: SolarBodyId): Promise<SolarTravel | null> {
    if (xr?.asksInScene) {
      return new Promise((resolve) => {
        asking = to
        askResolve = resolve
        host.onChange?.(state)
      })
    }
    return new Promise((resolve) => {
      comfort.innerHTML = `
        <div class="solar-comfort-box" role="dialog" aria-modal="true" aria-labelledby="solar-comfort-title">
          <h2 id="solar-comfort-title">${escapeHtml(t('solar.comfort.title'))}</h2>
          <p>${escapeHtml(t('solar.comfort.text'))}</p>
          <div class="solar-comfort-actions">
            <button type="button" class="solar-comfort-fly" data-travel="fly">${escapeHtml(t('solar.comfort.fly'))}</button>
            <button type="button" class="solar-comfort-fade" data-travel="fade">${escapeHtml(t('solar.comfort.fade'))}</button>
          </div>
        </div>`
      comfort.hidden = false
      const first = comfort.querySelector<HTMLElement>('.solar-comfort-fly')
      first?.focus()
      const done = (v: SolarTravel | null): void => {
        comfort.hidden = true
        comfort.removeEventListener('click', onClick)
        comfort.removeEventListener('keydown', onKey)
        resolve(v)
      }
      const onClick = (e: Event): void => {
        const b = (e.target as HTMLElement).closest<HTMLElement>('[data-travel]')
        if (b) done(b.dataset.travel as SolarTravel)
        else if (e.target === comfort) done(null)
      }
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') done(null)
      }
      comfort.addEventListener('click', onClick)
      comfort.addEventListener('keydown', onKey)
    })
  }

  function syncChrome(): void {
    // A note from the last arrival does not follow into the orrery or out of the mode.
    if (state !== 'body') hideArrival()
    card.hidden = state !== 'overview'
    strip.hidden = state !== 'body'
    document.body.classList.toggle('solar-overview-on', state === 'overview')
    document.body.classList.toggle('solar-strip-on', state === 'body')
    if (state === 'overview') renderCard()
    if (state === 'body') renderStrip()
    host.onChange?.(state)
  }

  // ---------------------------------------------------------------- view

  async function ensureView(): Promise<SolarViewHandle> {
    if (view) return view
    viewLoading ??= (async () => {
      const [THREE_] = await Promise.all([host.loadThree(), host.libraryReady().catch(() => {})])
      const v = await createSolarSystemView(THREE_, {
        container: host.container,
        before: host.uiRoot,
        nameOf: solarBodyName,
        pictureUrl: (id) => handle.pictureUrl(id),
        onPick: (id) => void handle.visit(id),
        onTime: () => syncDate(),
      })
      view = v
      return v
    })()
    return viewLoading
  }

  // ---------------------------------------------------------------- landing

  async function landingFor(id: SolarBodyId): Promise<string | null> {
    if (id === 'earth') return earthDatasetId
    for (const candidate of solarBody(id).landing) {
      if (!host.datasetExists(candidate)) continue
      if (await host.datasetLocked(candidate)) continue
      return candidate
    }
    return null
  }

  /** Opens the body's dataset; resolves when it has loaded or the wait is up. */
  async function land(id: SolarBodyId): Promise<void> {
    const dataset = await landingFor(id)
    ownLoads++
    const loading = (async () => {
      try {
        if (dataset) {
          if (host.currentDatasetId() !== dataset) await host.loadDataset(dataset)
        } else if (host.currentDatasetId()) {
          await host.clearDataset()
        }
      } finally {
        window.setTimeout(() => {
          ownLoads = Math.max(0, ownLoads - 1)
        }, 400)
      }
    })()
    await Promise.race([loading.catch(() => {}), new Promise((r) => window.setTimeout(r, LANDING_WAIT_MS))])
  }

  function noteEarthDataset(): void {
    if (host.currentDatasetBody() === null) earthDatasetId = host.currentDatasetId()
  }

  function scopeBrowse(id: SolarBodyId | null): void {
    host.setBrowseBodies(id ? solarBody(id).family : null)
  }

  function currentBodyNow(): SolarBodyId {
    return solarBodyOfDatasetBody(host.currentDatasetBody()) ?? 'earth'
  }

  // ---------------------------------------------------------------- handle

  /** The overview's clock and look carried from one presenter to the next. */
  function carryOver(from: SolarPresenter | null, to: SolarPresenter): void {
    if (!from || from === to) return
    to.setLayout(from.layout)
    to.setTime(from.timeMs)
    to.setRate(from.rate)
    to.setPlaying(from.playing)
  }

  const handle: SolarModeHandle = {
    get state() {
      return state
    },
    get body() {
      return body
    },
    get view() {
      return view
    },
    get travel() {
      return travel
    },
    get layout() {
      return current()?.layout ?? 'distances'
    },
    get timeMs() {
      return current()?.timeMs ?? Date.now()
    },
    get playing() {
      return current()?.playing ?? false
    },
    get rateKey() {
      return (RATES.find((r) => r.days === (current()?.rate ?? 1)) ?? RATES[0]).key
    },
    get asking() {
      return asking
    },
    get presenterNow() {
      return current()
    },

    async toggle() {
      if (state === 'overview') return handle.leave()
      return handle.enter()
    },

    async enter() {
      if (busy || state === 'overview') return
      busy = true
      try {
        if (state === 'off' || body === 'earth') noteEarthDataset()
        const v = await presenter()
        await v.fade(true)
        if (state === 'off') v.setTime(Date.now())
        v.show()
        state = 'overview'
        body = null
        scopeBrowse(null)
        syncChrome()
        // The last fade-in is only a look: the next press need not wait for it.
        busy = false
        await v.fade(false)
        if (!xr) card.querySelector<HTMLElement>('#solar-close')?.focus({ preventScroll: true })
      } finally {
        busy = false
      }
    },

    async leave() {
      if (busy || state === 'off') return
      busy = true
      try {
        const v = current()
        if (state === 'overview' && v) {
          await v.fade(true)
          v.hide()
          state = 'off'
          body = null
          scopeBrowse(null)
          syncChrome()
          busy = false
          await v.fade(false)
        } else {
          state = 'off'
          body = null
          scopeBrowse(null)
          syncChrome()
        }
      } finally {
        busy = false
      }
    },

    async visit(id) {
      if (busy) return
      if (host.mayChangeDataset && !host.mayChangeDataset()) {
        const msg = t('solar.meeting.locked')
        showArrival(msg)
        host.notice?.(msg)
        return
      }
      busy = true
      try {
        const v = await presenter()
        if (state === 'body' && body === 'earth') noteEarthDataset()
        await v.fade(true)
        await land(id)
        v.hide()
        state = 'body'
        body = id
        scopeBrowse(id)
        syncChrome()
        // The last fade-in is only a look: the next press need not wait for it.
        busy = false
        await v.fade(false)
      } finally {
        busy = false
      }
    },

    async travelTo(id) {
      if (busy || state !== 'body' || id === body) return
      if (host.mayChangeDataset && !host.mayChangeDataset()) {
        const msg = t('solar.meeting.locked')
        showArrival(msg)
        host.notice?.(msg)
        return
      }
      let how = travel
      if (!how) {
        how = await askTravel(id)
        if (!how) return
        handle.setTravel(how)
      }
      if (how === 'fade') return handle.visit(id)
      busy = true
      try {
        const from = body ?? currentBodyNow()
        if (from === 'earth') noteEarthDataset()
        const v = await presenter()
        const globe = host.globeOnScreen()
        await v.fade(true, FADE_MS * 0.6)
        // The arriving body's dataset loads under the flight.
        const landing = land(id)
        const trip = await v.fly({ from, to: id, globe })
        await landing
        state = 'body'
        body = id
        scopeBrowse(id)
        syncChrome()
        await v.reveal()
        showArrival(arrivalText(trip.distanceKm, trip.seconds))
      } finally {
        busy = false
      }
    },

    setTravel(next) {
      travel = next
      writeTravel(next)
    },

    setLayout(layout) {
      current()?.setLayout(layout)
      if (state === 'overview') renderCard()
    },

    today() {
      current()?.setTime(Date.now())
      syncDate()
    },

    togglePlay() {
      const p = current()
      if (!p) return
      p.setPlaying(!p.playing)
      if (state === 'overview') renderCard()
    },

    cycleRate() {
      const p = current()
      if (!p) return
      const i = RATES.findIndex((r) => r.days === p.rate)
      p.setRate(RATES[(i + 1) % RATES.length].days)
      if (state === 'overview') renderCard()
    },

    pictureUrl(id) {
      if (id === 'earth') return earthPictureUrl
      const sos = solarBody(id).picture
      return sos ? host.pictureUrl(sos) : null
    },

    answerTravel(choice) {
      const resolve = askResolve
      asking = null
      askResolve = null
      resolve?.(choice)
      host.onChange?.(state)
    },

    attachPresenter(next) {
      if (next === xr) return
      const before = current()
      if (asking) handle.answerTravel(null)
      xr = next
      const after = xr ?? view
      if (after) carryOver(before, after)
      // The orrery moves with the presenter: off the one that had it, onto the new one.
      if (state === 'overview') {
        if (before && before !== after) before.hide()
        if (after) after.show()
        else void presenter().then((v) => v.show())
      }
      syncChrome()
    },

    datasetChanged() {
      if (busy || ownLoads > 0 || state === 'off') return
      const now = solarBodyOfDatasetBody(host.currentDatasetBody())
      if (state === 'overview') {
        // A dataset opened from Browse while the orrery shows: go to it.
        if (!host.currentDatasetId()) return
        const v = current()
        if (!v) return
        void (async () => {
          busy = true
          try {
            await v.fade(true)
            v.hide()
            state = 'body'
            body = now
            scopeBrowse(now)
            syncChrome()
            busy = false
            await v.fade(false)
          } finally {
            busy = false
          }
        })()
        return
      }
      if (now && now !== body) {
        body = now
        scopeBrowse(now)
        syncChrome()
      }
    },

    relabel() {
      view?.relabel()
      xr?.relabel()
      if (state !== 'off') syncChrome()
    },
  }
  return handle
}

/** A date as the orrery shows it: "4 November 2026", in the reader's language. */
export function solarDateText(ms: number): string {
  return new Intl.DateTimeFormat(getLocale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(ms))
}

/** The Earth's sphere in the orrery: the app's own 2K day map. */
let earthPictureUrl: string | null = null
export function setSolarEarthPicture(url: string | null): void {
  earthPictureUrl = url
}
